import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { Section } from '../ui/Section';
import { Button } from '../ui/Button';
import { Quote, Star, MessageSquarePlus, X, CheckCircle2, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { useTestimonials, addTestimonial } from '../../hooks/useTestimonials';
import { publishToContentful } from '../../lib/contentful';
function toTitleCase(str: string): string {
  return str.replace(/\b\w/g, char => char.toUpperCase());
}
export function TestimonialsSection() {
  const {
    testimonials,
    loading
  } = useTestimonials();
  const [current, setCurrent] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [formStatus, setFormStatus] = useState<'idle' | 'publishing' | 'success'>('idle');
  const [direction, setDirection] = useState(1);
  const [form, setForm] = useState({
    name: '',
    titleRole: '',
    company: '',
    quote: '',
    rating: 0
  });
  const [ratingError, setRatingError] = useState(false);
  // Clamp current index if testimonials change
  useEffect(() => {
    if (testimonials.length > 0 && current >= testimonials.length) {
      setCurrent(testimonials.length - 1);
    }
  }, [testimonials.length, current]);
  // Auto-rotate
  useEffect(() => {
    if (showForm || testimonials.length <= 1) return;
    const timer = setInterval(() => {
      setDirection(1);
      setCurrent(prev => (prev + 1) % testimonials.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [testimonials.length, showForm]);
  const goNext = () => {
    setDirection(1);
    setCurrent(prev => (prev + 1) % testimonials.length);
  };
  const goPrev = () => {
    setDirection(-1);
    setCurrent(prev => (prev - 1 + testimonials.length) % testimonials.length);
  };
  const goTo = (index: number) => {
    setDirection(index > current ? 1 : -1);
    setCurrent(index);
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (form.rating === 0) {
      setRatingError(true);
      return;
    }
    setRatingError(false);
    setFormStatus('publishing');
    try {
      // Publish to Contentful CMS with correct field names
      await publishToContentful({
        contentType: 'testimonials',
        name: form.name,
        titlerole: form.titleRole,
        testimonial: form.quote,
        rating: form.rating,
        companyOrganization: form.company
      });
      // Also save locally for immediate display
      await addTestimonial({
        name: form.name,
        company: [form.titleRole, form.company].filter(Boolean).join(', '),
        quote: form.quote,
        rating: form.rating
      });
      toast.success('Thank you! Your testimonial has been submitted.');
      setFormStatus('success');
      setTimeout(() => {
        setFormStatus('idle');
        setShowForm(false);
        setForm({
          name: '',
          titleRole: '',
          company: '',
          quote: '',
          rating: 0
        });
      }, 3000);
    } catch (err: any) {
      setFormStatus('idle');
      toast.error(err?.message || 'Failed to submit. Please try again.');
    }
  };
  if (loading) {
    return <Section id="testimonials" background="white" data-id="element-263">
        <div className="flex items-center justify-center py-20 text-gray-400" data-id="element-264">
          <Loader2 className="w-5 h-5 animate-spin mr-2" data-id="element-265" />
          Loading testimonials…
        </div>
      </Section>;
  }
  if (testimonials.length === 0) return null;
  const safeIndex = Math.min(current, testimonials.length - 1);
  return <Section id="testimonials" background="white" data-id="element-266">
      <div className="max-w-4xl mx-auto text-center" data-id="element-267">
        <div className="mb-8 flex justify-center" data-id="element-268">
          <div className="w-12 h-12 bg-[#00316B]/10 rounded-full flex items-center justify-center text-[#00316B]" data-id="element-269">
            <Quote size={24} data-id="element-270" />
          </div>
        </div>

        <div className="relative min-h-[240px] flex flex-col items-center justify-center" data-id="element-271">
          {/* Left Arrow */}
          {testimonials.length > 1 && <button onClick={goPrev} className="absolute left-0 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-600 transition-colors z-10" aria-label="Previous testimonial" data-id="element-272">
              <ChevronLeft className="w-5 h-5" data-id="element-273" />
            </button>}

          {/* Right Arrow */}
          {testimonials.length > 1 && <button onClick={goNext} className="absolute right-0 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-600 transition-colors z-10" aria-label="Next testimonial" data-id="element-274">
              <ChevronRight className="w-5 h-5" data-id="element-275" />
            </button>}

          <div className="px-14" data-id="element-276">
            <AnimatePresence mode="wait" custom={direction} data-id="element-277">
              <motion.div key={safeIndex} custom={direction} initial={{
              opacity: 0,
              x: direction * 40
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: direction * -40
            }} transition={{
              duration: 0.35,
              ease: 'easeInOut'
            }} className="flex flex-col items-center" data-id="element-278">
                {/* Star Rating */}
                <div className="flex gap-1 mb-6" data-id="element-279">
                  {Array.from({
                  length: 5
                }).map((_, i) => <Star key={i} className={`w-5 h-5 ${i < testimonials[safeIndex].rating ? 'fill-[#F59E0B] text-[#F59E0B]' : 'text-gray-300'}`} data-id="element-280" />)}
                </div>

                <blockquote className="text-2xl md:text-3xl font-heading font-medium text-[#0C0C0C] leading-tight mb-8" data-id="element-281">
                  "{testimonials[safeIndex].quote}"
                </blockquote>

                <div className="font-bold text-[#0C0C0C] text-lg" data-id="element-282">
                  {testimonials[safeIndex].name}
                </div>
                <div className="text-sm text-gray-500 mt-1 font-medium" data-id="element-283">
                  {testimonials[safeIndex].company}
                </div>
                {testimonials[safeIndex].verified && <div className="mt-3 inline-flex items-center gap-1.5 text-xs text-[#00316B] font-medium" data-id="element-284">
                    <img src="https://cdn.magicpatterns.com/uploads/9mmk6QtVmSzZGQfVenZb4U/badge.svg" alt="Verified" className="w-4 h-4" data-id="element-285" />
                    Verified Testimonial
                  </div>}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        {/* Dot indicators */}
        <div className="flex justify-center gap-2 mt-10 mb-12" data-id="element-286">
          {testimonials.map((_, index) => <button key={index} onClick={() => goTo(index)} className={`w-2 h-2 rounded-full transition-all duration-300 ${index === safeIndex ? 'bg-[#00316B] w-6' : 'bg-gray-300 hover:bg-gray-400'}`} aria-label={`Go to testimonial ${index + 1}`} data-id="element-287" />)}
        </div>

        {/* Public Submission Button / Form */}
        <div className="mt-8" data-id="element-288">
          <AnimatePresence mode="wait" data-id="element-289">
            {!showForm ? <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -10
          }} data-id="element-290">
                <Button variant="outline" onClick={() => setShowForm(true)} leftIcon={<MessageSquarePlus className="w-4 h-4" data-id="element-292" />} data-id="element-291">
                  Write a Testimonial
                </Button>
              </motion.div> : <motion.div initial={{
            opacity: 0,
            height: 0
          }} animate={{
            opacity: 1,
            height: 'auto'
          }} exit={{
            opacity: 0,
            height: 0
          }} className="max-w-lg mx-auto bg-gray-50 rounded-2xl border border-gray-200 p-6 text-left relative overflow-hidden" data-id="element-293">
                <button onClick={() => setShowForm(false)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600" data-id="element-294">
                  <X className="w-5 h-5" data-id="element-295" />
                </button>

                {formStatus === 'success' ? <div className="flex flex-col items-center justify-center py-10 text-center" data-id="element-296">
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4" data-id="element-297">
                      <CheckCircle2 className="w-6 h-6 text-green-600" data-id="element-298" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-299">
                      Thank You!
                    </h3>
                    <p className="text-gray-600" data-id="element-300">
                      Your testimonial has been submitted.
                    </p>
                  </div> : <>
                    <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-301">
                      Share Your Experience
                    </h3>
                    <form onSubmit={handleSubmit} className="space-y-4" data-id="element-302">
                      <div data-id="element-303">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-304">
                          Your Name <span className="text-red-500" data-id="element-305">*</span>
                        </label>
                        <input type="text" required className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00316B] focus:border-transparent outline-none transition-all" placeholder="e.g. John Doe" value={form.name} onChange={e => setForm({
                    ...form,
                    name: toTitleCase(e.target.value)
                  })} disabled={formStatus === 'publishing'} data-id="element-306" />
                      </div>

                      <div data-id="element-307">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-308">
                          Title / Role
                        </label>
                        <input type="text" className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00316B] focus:border-transparent outline-none transition-all" placeholder="e.g. Head of Marketing" value={form.titleRole} onChange={e => setForm({
                    ...form,
                    titleRole: toTitleCase(e.target.value)
                  })} disabled={formStatus === 'publishing'} data-id="element-309" />
                      </div>

                      <div data-id="element-310">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-311">
                          Company / Organization{' '}
                          <span className="text-red-500" data-id="element-312">*</span>
                        </label>
                        <input type="text" required className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00316B] focus:border-transparent outline-none transition-all" placeholder="e.g. Acme Corp" value={form.company} onChange={e => setForm({
                    ...form,
                    company: toTitleCase(e.target.value)
                  })} disabled={formStatus === 'publishing'} data-id="element-313" />
                      </div>

                      <div data-id="element-314">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-315">
                          Rating <span className="text-red-500" data-id="element-316">*</span>
                        </label>
                        <div className="flex gap-1" data-id="element-317">
                          {[1, 2, 3, 4, 5].map(star => <button key={star} type="button" onClick={() => {
                      setForm({
                        ...form,
                        rating: star
                      });
                      setRatingError(false);
                    }} className="focus:outline-none transition-transform active:scale-90" disabled={formStatus === 'publishing'} data-id="element-318">
                              <Star className={`w-6 h-6 ${star <= form.rating ? 'fill-[#F59E0B] text-[#F59E0B]' : 'text-gray-300'}`} data-id="element-319" />
                            </button>)}
                        </div>
                        {ratingError && <p className="text-xs text-red-500 mt-1" data-id="element-320">
                            Please select a rating
                          </p>}
                      </div>

                      <div data-id="element-321">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-322">
                          Testimonial <span className="text-red-500" data-id="element-323">*</span>
                        </label>
                        <textarea required rows={3} className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00316B] focus:border-transparent outline-none transition-all resize-none" placeholder="How was your experience working with us?" value={form.quote} onChange={e => setForm({
                    ...form,
                    quote: e.target.value
                  })} disabled={formStatus === 'publishing'} data-id="element-324" />
                      </div>

                      <div className="pt-2" data-id="element-325">
                        <Button type="submit" variant="secondary" className="w-full" isLoading={formStatus === 'publishing'} disabled={formStatus === 'publishing'} data-id="element-326">
                          {formStatus === 'publishing' ? 'Publishing…' : 'Submit Testimonial'}
                        </Button>
                      </div>
                    </form>
                  </>}
              </motion.div>}
          </AnimatePresence>
        </div>
      </div>
    </Section>;
}